import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent, 
    DialogActions, TextField, MenuItem, Select, FormControl, InputLabel,
    Container, Stack, Avatar, Tooltip, useTheme, useMediaQuery, CircularProgress, Grid
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Schedule as ScheduleIcon,
    Class as ClassIcon,
    Room as RoomIcon,
    Person as PersonIcon,
    FilterList as FilterListIcon,
    EventNote as EventNoteIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useToast } from '../../../context/ToastContext';
import { CustomNepaliDatePicker, formatDatePart } from '../../../utils/customDate';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const ShowRoutines = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [routines, setRoutines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingRoutine, setEditingRoutine] = useState(null);
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedDay, setSelectedDay] = useState('');
    
    const { showToast } = useToast();
    
    const [formData, setFormData] = useState({
        classId: '',
        routineType: 'class',
        day: 'Sunday',
        period: '1',
        startTime: '',
        endTime: '',
        subject: '',
        teacherId: '',
        room: '',
        examDate: ''
    });

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const periods = ['1', '2', '3', '4', '5', '6', '7', '8'];

    useEffect(() => {
        fetchRoutines();
        fetchClasses();
    }, []);

    const fetchRoutines = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/Routines/${localStorage.getItem('schoolId')}`);
            setRoutines(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching routines:', error);
            setLoading(false);
        }
    };

    const fetchClasses = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/classes/${localStorage.getItem('schoolId')}`);
            setClasses(response.data);
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const handleOpenDialog = (routine = null) => {
        if (routine) {
            setEditingRoutine(routine);
            setFormData({
                classId: routine.classId?._id || routine.classId,
                routineType: routine.routineType,
                day: routine.day,
                period: routine.period,
                startTime: routine.startTime,
                endTime: routine.endTime,
                subject: routine.subject,
                teacherId: routine.teacherId,
                room: routine.room,
                examDate: routine.examDate || ''
            });
        } else {
            setEditingRoutine(null);
            setFormData({
                classId: '',
                routineType: 'class',
                day: 'Sunday',
                period: '1',
                startTime: '',
                endTime: '',
                subject: '',
                teacherId: '',
                room: '',
                examDate: ''
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingRoutine(null);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        try {
            if (editingRoutine) {
                await axios.put(`${process.env.REACT_APP_BASE_URL}/Routine/${editingRoutine._id}`, formData);
                showToast("Routine updated successfully!", "success");
            } else {
                await axios.post(`${process.env.REACT_APP_BASE_URL}/Routine/Create`, {
                    ...formData,
                    schoolId: localStorage.getItem('schoolId')
                });
                showToast("Routine created successfully!", "success");
            }
            fetchRoutines();
            handleCloseDialog();
        } catch (error) {
            console.error('Error saving routine:', error);
            showToast("Failed to save routine.", "error");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this routine?')) {
            try {
                await axios.delete(`${process.env.REACT_APP_BASE_URL}/Routine/${id}`);
                showToast("Routine deleted successfully!", "success");
                fetchRoutines();
            } catch (error) {
                console.error('Error deleting routine:', error);
                showToast("Failed to delete routine.", "error");
            }
        }
    };

    const getRoutineTypeColor = (type) => {
        switch (type) {
            case 'class': return { bg: '#e3f2fd', color: '#1976d2' };
            case 'exam': return { bg: '#f3e5f5', color: '#9c27b0' };
            case 'assembly': return { bg: '#e8f5e9', color: '#2e7d32' };
            case 'break': return { bg: '#fff3e0', color: '#ed6c02' };
            default: return { bg: '#f5f5f5', color: '#757575' };
        }
    };

    const getClassName = (classId) => {
        const cls = classes.find(c => c._id === classId || c._id === (classId?._id));
        return cls ? cls.sclassName || cls.className : 'N/A';
    };

    const filteredRoutines = routines.filter(r => {
        const matchesClass = !selectedClass || (r.classId?._id || r.classId) === selectedClass;
        const matchesDay = !selectedDay || r.day === selectedDay;
        return matchesClass && matchesDay;
    });

    if (loading && routines.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
                <CircularProgress sx={{ color: '#667eea', mb: 2 }} />
                <Typography color="textSecondary">Loading scheduling data...</Typography>
            </Box>
        );
    }

    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} spacing={2} sx={{ py: 4 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 0.5 }}>
                                    Routine Management
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    Coordinate classes, exams, and daily schedules
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <Button 
                                variant="contained" 
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{ bgcolor: 'white', color: '#1a1a2e', '&:hover': { bgcolor: '#f0f0f0' }, borderRadius: '12px', px: 3, fontWeight: 700 }}
                            >
                                Add Slot
                            </Button>
                            <Button 
                                variant="outlined" 
                                startIcon={<EventNoteIcon />}
                                onClick={() => navigate("/Admin/addroutine")}
                                sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }, borderRadius: '12px' }}
                            >
                                Grid View
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Container maxWidth="xl" sx={{ mt: 5, pb: 10 }}>
                <Grid container spacing={3}>
                    {/* Filters */}
                    <Grid item xs={12}>
                        <FilterPaper elevation={1}>
                            <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center" sx={{ p: 2 }}>
                                <FilterListIcon sx={{ color: 'text.secondary' }} />
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Filter by Class</InputLabel>
                                    <Select value={selectedClass} label="Filter by Class" onChange={(e) => setSelectedClass(e.target.value)}>
                                        <MenuItem value="">All Classes</MenuItem>
                                        {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.sclassName || c.className}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Filter by Day</InputLabel>
                                    <Select value={selectedDay} label="Filter by Day" onChange={(e) => setSelectedDay(e.target.value)}>
                                        <MenuItem value="">All Days</MenuItem>
                                        {days.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Box sx={{ flexGrow: 1 }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    Found {filteredRoutines.length} active slots
                                </Typography>
                            </Stack>
                        </FilterPaper>
                    </Grid>

                    {/* Routine Table */}
                    <Grid item xs={12}>
                        <StyledTableContainer component={Paper} elevation={2}>
                            <Table sx={{ minWidth: 1000 }}>
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <StyledTableCell>Class & Type</StyledTableCell>
                                        <StyledTableCell>Time & Period</StyledTableCell>
                                        <StyledTableCell>Subject Details</StyledTableCell>
                                        <StyledTableCell>Location</StyledTableCell>
                                        <StyledTableCell align="center">Actions</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRoutines.map((routine, idx) => {
                                        const typeStyle = getRoutineTypeColor(routine.routineType);
                                        return (
                                            <TableRow key={idx} hover sx={{ transition: '0.2s', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{getClassName(routine.classId)}</Typography>
                                                        <Chip 
                                                            label={routine.routineType.toUpperCase()} 
                                                            size="small" 
                                                            sx={{ bgcolor: typeStyle.bg, color: typeStyle.color, fontWeight: 800, fontSize: '0.65rem', width: 'fit-content' }} 
                                                        />
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <ScheduleIcon sx={{ fontSize: 14, color: '#667eea' }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{routine.startTime} - {routine.endTime}</Typography>
                                                        </Stack>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {routine.routineType === 'exam' && routine.examDate ? `${formatDatePart(routine.examDate)} • ` : ''}
                                                            {routine.day} • Period {routine.period}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>{routine.subject}</Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            <Typography variant="caption" color="textSecondary">Teacher ID: {routine.teacherId}</Typography>
                                                        </Stack>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <RoomIcon sx={{ fontSize: 16, color: '#f44336' }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Room: {routine.room}</Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Stack direction="row" spacing={1} justifyContent="center">
                                                        <IconButton size="small" onClick={() => handleOpenDialog(routine)} sx={{ color: '#667eea', '&:hover': { bgcolor: '#f0f4ff' } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => handleDelete(routine._id)} sx={{ color: '#f44336', '&:hover': { bgcolor: '#ffebee' } }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {filteredRoutines.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                                <EventNoteIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                                <Typography color="textSecondary">No routine entries found for the selected criteria.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </Grid>
                </Grid>
            </Container>

            {/* Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ bgcolor: '#f8fafc', fontWeight: 800 }}>{editingRoutine ? 'Update Slot' : 'Create Routine Slot'}</DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Class</InputLabel>
                                <Select name="classId" value={formData.classId} onChange={handleChange} label="Class">
                                    {classes.map(cls => <MenuItem key={cls._id} value={cls._id}>{cls.sclassName || cls.className}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select name="routineType" value={formData.routineType} onChange={handleChange} label="Type">
                                    <MenuItem value="class">Class</MenuItem>
                                    <MenuItem value="exam">Exam</MenuItem>
                                    <MenuItem value="assembly">Assembly</MenuItem>
                                    <MenuItem value="break">Break</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Day</InputLabel>
                                <Select name="day" value={formData.day} onChange={handleChange} label="Day">
                                    {days.map(day => <MenuItem key={day} value={day}>{day}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Period</InputLabel>
                                <Select name="period" value={formData.period} onChange={handleChange} label="Period">
                                    {periods.map(period => <MenuItem key={period} value={period}>{period}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Start Time" name="startTime" type="time" value={formData.startTime} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="End Time" name="endTime" type="time" value={formData.endTime} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Subject" name="subject" value={formData.subject} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Teacher ID" name="teacherId" value={formData.teacherId} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Room" name="room" value={formData.room} onChange={handleChange} />
                        </Grid>
                        {formData.routineType === 'exam' && (
                            <Grid item xs={12}>
                                <CustomNepaliDatePicker
                                    label="Exam Date"
                                    value={formData.examDate}
                                    onChange={(val) => setFormData({ ...formData, examDate: val })}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    <Button onClick={handleCloseDialog} sx={{ fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '10px', bgcolor: '#667eea' }}>
                        {editingRoutine ? 'Update Slot' : 'Create Slot'}
                    </Button>
                </DialogActions>
            </Dialog>

        </PageContainer>
    );
};

export default ShowRoutines;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); border-radius: 0 0 40px 40px !important; padding-bottom: 40px;`;
const FilterPaper = styled(Paper)`border-radius: 16px !important; overflow: hidden;`;
const StyledTableContainer = styled(TableContainer)`border-radius: 20px !important; overflow: hidden;`;
const StyledTableCell = styled(TableCell)`font-weight: 700 !important; color: #475569 !important; padding: 16px !important;`;
